import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card } from '../ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Plus, Trash2, X, Printer, ChevronDown, ChevronRight, Search, Pencil } from 'lucide-react';
import { formatDate } from '../../utils/dateFormatter';
import { useOrderPrint } from './OrderPrint';
import { canDelete, canSeePrice, canEditOrder } from '../../utils/permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Orders = () => {
  const { getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const { handlePrint } = useOrderPrint();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [variants, setVariants] = useState([{ size: '', color: '', quantity: 1 }]);
  const [formData, setFormData] = useState({
    order_number: '',
    item_code: '',
    item_price: '',
    card_number: '',
    client_id: '',
    client_name: '',
    items: '',
    status: 'pending',
    procurement_date: '',
    notes: ''
  });
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const [deliveryHistory, setDeliveryHistory] = useState({}); // keyed by order.id
  const [editOrder, setEditOrder] = useState(null);
  const [editFormData, setEditFormData] = useState({});

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, { headers: getAuthHeader() });
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to fetch orders');
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, { headers: getAuthHeader() });
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to fetch clients');
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchClients();
  }, []);

  const addVariant = () => {
    setVariants([...variants, { size: '', color: '', quantity: 1 }]);
  };

  const removeVariant = (index) => {
    setVariants(variants.filter((_, i) => i !== index));
  };

  const updateVariant = (index, field, value) => {
    const newVariants = [...variants];
    newVariants[index][field] = field === 'quantity' ? parseInt(value) : value;
    setVariants(newVariants);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (clients.length === 0) {
      toast.error('Please add a client first before creating orders');
      return;
    }

    if (!formData.client_name) {
      toast.error('Please select a client');
      return;
    }

    try {
      const orderData = {
        ...formData,
        variants,
        item_price: formData.item_price === '' ? null : parseFloat(formData.item_price),
      };
      await axios.post(`${API}/orders`, orderData, { headers: getAuthHeader() });
      toast.success('Order added successfully');
      setIsOpen(false);
      fetchOrders();
      setFormData({
        order_number: '',
        item_code: '',
        item_price: '',
        card_number: '',
        client_id: '',
        client_name: '',
        items: '',
        status: 'pending',
        procurement_date: '',
        production_date: '',
        delivery_date: '',
        notes: ''
      });
      setVariants([{ size: '', color: '', quantity: 1 }]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add order');
    }
  };

  const handleDelete = async (order_number) => {
    if (user.role !== 'admin') {
      toast.error('Only admin can delete');
      return;
    }
    try {
      await axios.delete(`${API}/orders/${order_number}`, { headers: getAuthHeader() });
      toast.success('Order deleted');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const handleStatusUpdate = async (order_number, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${order_number}/status`, { status: newStatus }, { headers: getAuthHeader() });
      toast.success(`Order marked as ${newStatus}`);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const openEdit = (order) => {
    setEditOrder(order);
    setEditFormData({
      order_number: order.order_number,
      item_code: order.item_code || '',
      item_price: order.item_price ?? '',
      card_number: order.card_number || '',
      client_id: order.client_id,
      client_name: order.client_name,
      items: order.items || '',
      status: order.status,
      procurement_date: order.procurement_date || '',
      production_date: order.production_date || '',
      delivery_date: order.delivery_date || '',
      notes: order.notes || '',
      variants: order.variants || [],
    });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/orders/${editOrder.order_number}`, editFormData, { headers: getAuthHeader() });
      toast.success('Order updated');
      setEditOrder(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update order');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      pending: 'bg-warning/20 text-warning',
      fulfilled: 'bg-success/20 text-success',
      cancelled: 'bg-destructive/20 text-destructive'
    };
    return statusColors[status] || statusColors.pending;
  };

  const filteredOrders = orders.filter(o => {
    const matchesTab = activeTab === 'all' || o.status === activeTab;
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q ||
      o.order_number?.toLowerCase().includes(q) ||
      o.client_name?.toLowerCase().includes(q) ||
      o.items?.toLowerCase().includes(q) ||
      o.item_code?.toLowerCase().includes(q) ||
      o.card_number?.toLowerCase().includes(q);
    return matchesTab && matchesSearch;
  });

  const toggleExpand = async (order) => {
    const isOpening = expandedRow !== order.order_number;
    setExpandedRow(prev => prev === order.order_number ? null : order.order_number);
    if (isOpening && order.id && deliveryHistory[order.id] === undefined) {
      setDeliveryHistory(prev => ({ ...prev, [order.id]: null })); // null = loading
      try {
        const res = await axios.get(`${API}/orders/${order.id}/delivery-notes`, { headers: getAuthHeader() });
        setDeliveryHistory(prev => ({ ...prev, [order.id]: res.data }));
      } catch {
        setDeliveryHistory(prev => ({ ...prev, [order.id]: [] }));
      }
    }
  };

  return (
    <Card className="p-6 border border-border">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold" lang={language === 'ar' ? 'ar' : 'en'}>{t('orders')}</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button
              className="gap-2 bg-accent hover:bg-accent/90"
              data-testid="add-order-btn"
              disabled={clients.length === 0}
            >
              <Plus className="w-4 h-4" />
              <span lang={language === 'ar' ? 'ar' : 'en'}>{t('add')}</span>
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
            <DialogHeader>
              <DialogTitle lang={language === 'ar' ? 'ar' : 'en'}>{t('addOrder')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>Order Number</Label>
                  <Input value={formData.order_number} onChange={(e) => setFormData({...formData, order_number: e.target.value})} required data-testid="order-number-input" />
                </div>
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>Card Number</Label>
                  <Input value={formData.card_number} onChange={(e) => setFormData({...formData, card_number: e.target.value})} data-testid="order-card-number-input" placeholder="Optional" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>Item Code</Label>
                  <Input value={formData.item_code} onChange={(e) => setFormData({...formData, item_code: e.target.value})} required data-testid="order-item-code-input" placeholder="e.g. TSH001" />
                </div>
                {canSeePrice(user?.role) && (
                  <div>
                    <Label lang={language === 'ar' ? 'ar' : 'en'}>Item Price (EGP)</Label>
                    <Input type="number" step="0.01" value={formData.item_price} onChange={(e) => setFormData({...formData, item_price: parseFloat(e.target.value)})} data-testid="order-item-price-input" />
                  </div>
                )}
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Client</Label>
                <select
                  value={formData.client_id}
                  onChange={(e) => {
                    const sel = clients.find(c => c.id === e.target.value);
                    setFormData({...formData, client_id: e.target.value, client_name: sel?.name || ''});
                  }}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  required
                  data-testid="order-client-select"
                >
                  <option value="">Select Client</option>
                  {clients.map((client, idx) => (
                    <option key={idx} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Description</Label>
                <Input value={formData.items} onChange={(e) => setFormData({...formData, items: e.target.value})} required data-testid="order-items-input" placeholder="e.g. T-shirts, Hoodies" />
              </div>

              <div className="border border-border rounded-md p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <Label lang={language === 'ar' ? 'ar' : 'en'}>Size & Color Variants</Label>
                  <Button type="button" size="sm" variant="outline" onClick={addVariant} data-testid="add-variant-btn">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {variants.map((variant, idx) => (
                  <div key={idx} className="flex gap-2 items-center" data-testid={`variant-${idx}`}>
                    <Input
                      placeholder="Size (e.g. S, M, L, 32, 34)"
                      value={variant.size}
                      onChange={(e) => updateVariant(idx, 'size', e.target.value)}
                      required
                      data-testid={`variant-size-${idx}`}
                    />
                    <Input
                      placeholder="Color (e.g. Black, Navy)"
                      value={variant.color}
                      onChange={(e) => updateVariant(idx, 'color', e.target.value)}
                      required
                      data-testid={`variant-color-${idx}`}
                    />
                    <Input
                      type="number"
                      placeholder="Qty"
                      value={variant.quantity}
                      onChange={(e) => updateVariant(idx, 'quantity', e.target.value)}
                      required
                      className="w-24"
                      data-testid={`variant-qty-${idx}`}
                    />
                    {variants.length > 1 && (
                      <Button type="button" size="sm" variant="ghost" onClick={() => removeVariant(idx)} data-testid={`remove-variant-${idx}`}>
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Procurement Date</Label>
                <Input type="date" value={formData.procurement_date} onChange={(e) => setFormData({...formData, procurement_date: e.target.value})} data-testid="order-procurement-date" />
              </div>

              <div>
                <Label lang={language === 'ar' ? 'ar' : 'en'}>Notes</Label>
                <Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} data-testid="order-notes-input" />
              </div>

              <Button type="submit" className="w-full bg-accent hover:bg-accent/90" data-testid="order-submit-btn">
                <span lang={language === 'ar' ? 'ar' : 'en'}>{t('save')}</span>
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {clients.length === 0 && (
        <Card className="p-6 border border-warning bg-warning/5 mb-4">
          <p className="text-sm text-warning" lang={language === 'ar' ? 'ar' : 'en'}>
            ⚠️ Please add clients first before creating orders. Go to Finance → Clients to add clients.
          </p>
        </Card>
      )}

      {/* Search + filter */}
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by order #, client, or item..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <select
          value={activeTab}
          onChange={e => setActiveTab(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm"
        >
          <option value="all">All ({orders.length})</option>
          <option value="pending">Pending ({orders.filter(o => o.status === 'pending').length})</option>
          <option value="fulfilled">Fulfilled ({orders.filter(o => o.status === 'fulfilled').length})</option>
          <option value="cancelled">Cancelled ({orders.filter(o => o.status === 'cancelled').length})</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-muted/50 text-left">
              <th className="p-3 w-8"></th>
              <th className="p-3">Order #</th>
              <th className="p-3">Card #</th>
              <th className="p-3">Client</th>
              <th className="p-3">Item Code</th>
              {canSeePrice(user?.role) && <th className="p-3">Price</th>}
              <th className="p-3">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.map((order, idx) => (
              <React.Fragment key={order.order_number}>
                <tr
                  className="border-t border-border hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => toggleExpand(order)}
                  data-testid={`order-row-${idx}`}
                >
                  <td className="p-3 text-muted-foreground">
                    {expandedRow === order.order_number
                      ? <ChevronDown className="w-4 h-4" />
                      : <ChevronRight className="w-4 h-4" />}
                  </td>
                  <td className="p-3 font-medium">#{order.order_number}</td>
                  <td className="p-3 text-muted-foreground">{order.card_number || '—'}</td>
                  <td className="p-3">{order.client_name}</td>
                  <td className="p-3">
                    {order.item_code || '—'}
                    {order.items && <span className="text-xs text-muted-foreground ml-1">({order.items})</span>}
                  </td>
                  {canSeePrice(user?.role) && (
                    <td className="p-3 whitespace-nowrap">
                      {order.item_price != null
                        ? <>{order.item_price.toFixed(2)} {t('currency')}</>
                        : <span className="text-xs text-amber-600 font-medium">No Price</span>
                      }
                    </td>
                  )}
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded text-xs ${getStatusBadge(order.status)}`} lang={language === 'ar' ? 'ar' : 'en'}>
                      {t(order.status)}
                    </span>
                  </td>
                  <td className="p-3" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      {order.status === 'pending' && !order.production_date && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-xs"
                          onClick={async () => {
                            try {
                              await axios.patch(`${API}/orders/${order.order_number}/production-date`, {}, { headers: getAuthHeader() });
                              toast.success('Production date set');
                              fetchOrders();
                            } catch { toast.error('Failed to set production date'); }
                          }}
                        >
                          In Production
                        </Button>
                      )}
                      {order.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-destructive text-destructive hover:bg-destructive/10 text-xs"
                          onClick={() => handleStatusUpdate(order.order_number, 'cancelled')}
                          data-testid={`cancel-order-${idx}`}
                        >
                          <span lang={language === 'ar' ? 'ar' : 'en'}>Cancel</span>
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => handlePrint(order)} className="gap-1">
                        <Printer className="w-4 h-4" />
                      </Button>
                      {canEditOrder(user?.role) && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(order)} data-testid={`edit-order-${idx}`}>
                          <Pencil className="w-4 h-4 text-muted-foreground" />
                        </Button>
                      )}
                      {canDelete(user?.role) && (
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(order.order_number)} data-testid={`delete-order-${idx}`}>
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>

                {/* Expanded detail row */}
                {expandedRow === order.order_number && (
                  <tr className="border-t border-border bg-muted/20">
                    <td colSpan={8} className="p-4">
                      <div className="grid grid-cols-3 gap-4 text-sm mb-4">
                        <div><span className="font-medium">Procurement:</span> {order.procurement_date ? formatDate(order.procurement_date) : '—'}</div>
                        <div><span className="font-medium">Production:</span> {order.production_date ? formatDate(order.production_date) : '—'}</div>
                        <div><span className="font-medium">Notes:</span> {order.notes || '—'}</div>
                      </div>
                      <table className="text-sm border-collapse">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="border border-border px-3 py-1 text-left">Size</th>
                            <th className="border border-border px-3 py-1 text-left">Color</th>
                            <th className="border border-border px-3 py-1 text-right">Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.variants?.map((v, vidx) => (
                            <tr key={vidx}>
                              <td className="border border-border px-3 py-1">{v.size}</td>
                              <td className="border border-border px-3 py-1">{v.color}</td>
                              <td className="border border-border px-3 py-1 text-right">{v.quantity}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <div className="mt-4">
                        <p className="text-sm font-medium mb-2">Delivery History</p>
                        {deliveryHistory[order.id] === null ? (
                          <p className="text-xs text-muted-foreground">Loading...</p>
                        ) : !deliveryHistory[order.id] || deliveryHistory[order.id].length === 0 ? (
                          <p className="text-xs text-muted-foreground">No deliveries yet</p>
                        ) : (
                          <table className="text-sm border-collapse">
                            <thead>
                              <tr className="bg-muted/50">
                                <th className="border border-border px-3 py-1 text-left">Note #</th>
                                <th className="border border-border px-3 py-1 text-left">Date</th>
                                <th className="border border-border px-3 py-1 text-left">Items Delivered</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deliveryHistory[order.id].map((dn, i) => (
                                <tr key={i}>
                                  <td className="border border-border px-3 py-1 whitespace-nowrap">{dn.note_number}</td>
                                  <td className="border border-border px-3 py-1 whitespace-nowrap">{formatDate(dn.delivery_date)}</td>
                                  <td className="border border-border px-3 py-1">
                                    {dn.items.map((item, j) => (
                                      <span key={j} className="mr-3 text-xs">{item.description}: <strong>{item.quantity}</strong></span>
                                    ))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {filteredOrders.length === 0 && (
        <div className="text-center py-12 text-muted-foreground" data-testid="empty-orders">
          <p lang={language === 'ar' ? 'ar' : 'en'}>
            {searchQuery || activeTab !== 'all' ? 'No orders match your filter.' : 'No orders yet'}
          </p>
        </div>
      )}

      {/* Edit Order Dialog */}
      <Dialog open={!!editOrder} onOpenChange={(open) => !open && setEditOrder(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Order #{editOrder?.order_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Item Code</Label>
                <Input value={editFormData.item_code || ''} onChange={(e) => setEditFormData({...editFormData, item_code: e.target.value})} required />
              </div>
              <div>
                <Label>Item Price (EGP)</Label>
                <Input type="number" step="0.01" value={editFormData.item_price ?? ''} onChange={(e) => setEditFormData({...editFormData, item_price: e.target.value === '' ? null : parseFloat(e.target.value)})} placeholder="Enter price" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Card Number</Label>
                <Input value={editFormData.card_number || ''} onChange={(e) => setEditFormData({...editFormData, card_number: e.target.value})} placeholder="Optional" />
              </div>
              <div>
                <Label>Status</Label>
                <select
                  value={editFormData.status || 'pending'}
                  onChange={(e) => setEditFormData({...editFormData, status: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="pending">Pending</option>
                  <option value="fulfilled">Fulfilled</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={editFormData.items || ''} onChange={(e) => setEditFormData({...editFormData, items: e.target.value})} required />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Procurement Date</Label>
                <Input type="date" value={editFormData.procurement_date || ''} onChange={(e) => setEditFormData({...editFormData, procurement_date: e.target.value})} />
              </div>
              <div>
                <Label>Production Date</Label>
                <Input type="date" value={editFormData.production_date || ''} onChange={(e) => setEditFormData({...editFormData, production_date: e.target.value})} />
              </div>
              <div>
                <Label>Delivery Date</Label>
                <Input type="date" value={editFormData.delivery_date || ''} onChange={(e) => setEditFormData({...editFormData, delivery_date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Input value={editFormData.notes || ''} onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})} />
            </div>
            <Button type="submit" className="w-full bg-accent hover:bg-accent/90">Save Changes</Button>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
